import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, Input, Output, EventEmitter, ViewChild, ViewRef } from '@angular/core';

/** Uma entrada da barra (fora os destaques, que são sempre a primeira). */
export interface CategoryNavEntry {
	/** O valor que a página usa para rolar: 'combos', 'pizzas' ou `<nome>-<id>`. */
	value: string;
	kind: 'combo' | 'pizza' | 'generic';
	/** id do botão no layout legado (`elementScrollCategoryNav`). */
	legacyId: string;
	category: any;
	/** Índice em `categories` (o `selectedCategory` do legado). */
	categoryIndex: number;
}

@Component({
	selector: 'category-nav',
	templateUrl: './category-nav.component.html',
	styleUrls: ['./category-nav.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryNavComponent implements OnInit {
	@Input() itemsToHide;
	@Input() categories;
	@Input() comboCategoryName = 'Combos';
	@Input() pizzaCategoryName = 'Pizzas';
	@Input() stickyMode = false;
	/**
	 * Ordem das entradas ('combos', 'pizzas', `<nome>-<id>`), da ordem única do cardápio
	 * (`MenuPage.menuSectionOrder`). Vazia: combos, pizzas e `categories`, como sempre foi.
	 * O que faltar nela vai para o fim, então nenhuma categoria some da barra.
	 */
	@Input() sectionOrder: string[] | null = null;

	@Input() isNewHomePedirDelivery = false;
	@Input() set activeCategoryValue(value: string) {
		this.markActive(value);
	}

	@ViewChild('pillsWrapper') pillsWrapper: ElementRef<HTMLElement>;

	categoryToScroll: string = '';
	categoryToCentralizeInNav: string;
	selectedCategory: any;
	selectId = 'categoryNav';

	@Output() scrollToCategory = new EventEmitter<string>();

	constructor(private cdr: ChangeDetectorRef) { }

	ngOnInit() {
		if (this.categoryToScroll) return;

		if (this.itemsToHide && !this.itemsToHide.featuredItems) { this.categoryToScroll = 'featuredItems'; return; }
		const firstVisibleEntry = this.navEntries.find(entry => this.isEntryVisible(entry));
		if (firstVisibleEntry) this.categoryToScroll = firstVisibleEntry.value;
	}

	/**
	 * Entradas na ordem da tela. Memo pela referência E pelo tamanho de `categories` (a página
	 * preenche o array por push) e pela referência de `sectionOrder`.
	 */
	get navEntries(): CategoryNavEntry[] {
		const categories = this.categories || [];
		const memo = this.navEntriesMemo;
		if (!memo || memo.categories !== categories || memo.categoriesLength !== categories.length || memo.sectionOrder !== this.sectionOrder) {
			this.navEntriesMemo = { categories, categoriesLength: categories.length, sectionOrder: this.sectionOrder, entries: this.buildNavEntries(categories) };
		}
		return this.navEntriesMemo.entries;
	}
	private navEntriesMemo: { categories: any[]; categoriesLength: number; sectionOrder: string[] | null; entries: CategoryNavEntry[] } | null = null;

	private buildNavEntries(categories: any[]): CategoryNavEntry[] {
		const categoryValueOf = (category: any) => category.name + '-' + category.id;
		const genericByValue = new Map<string, number>(categories.map((category, categoryIndex): [string, number] => [categoryValueOf(category), categoryIndex]));
		const orderedValues = [...(this.sectionOrder || []), 'combos', 'pizzas', ...categories.map(categoryValueOf)];
		const seenValues = new Set<string>();
		const entries: CategoryNavEntry[] = [];

		for (const value of orderedValues) {
			if (seenValues.has(value)) continue;
			seenValues.add(value);

			if (value === 'combos') {
				entries.push({ value, kind: 'combo', legacyId: 'combos-category', category: null, categoryIndex: -1 });
			} else if (value === 'pizzas') {
				entries.push({ value, kind: 'pizza', legacyId: 'pizzas-category', category: null, categoryIndex: -1 });
			} else if (genericByValue.has(value)) {
				const categoryIndex = genericByValue.get(value);
				const category = categories[categoryIndex];
				entries.push({ value, kind: 'generic', legacyId: 'general-' + category.id, category, categoryIndex });
			}
		}
		return entries;
	}

	isEntryVisible(entry: CategoryNavEntry): boolean {
		if (entry.kind === 'combo') return !this.itemsToHide?.combos;
		if (entry.kind === 'pizza') return !this.itemsToHide?.pizzas;
		return this.showCategory(entry.category.products);
	}

	entryLabel(entry: CategoryNavEntry): string {
		if (entry.kind === 'combo') return this.comboCategoryName;
		if (entry.kind === 'pizza') return this.pizzaCategoryName;
		return entry.category.name;
	}

	/** Toque no segmento do legado: mesmo efeito dos três botões fixos de antes. */
	selectLegacyEntry(entry: CategoryNavEntry) {
		this.selectCategory(entry.value);
		this.selectedCategory = entry.kind === 'generic' ? entry.categoryIndex : entry.value;
		this.categoryToCentralizeInNav = entry.legacyId;
	}

	trackByEntry(_index: number, entry: CategoryNavEntry) {
		return entry.value;
	}

	/**
	 * Volta a marcar a categoria em que a página ESTÁ mesmo sem o input `activeCategoryValue`
	 * mudar — o caso da pill cancelada (`MenuPage.showCategoryInView`). A escolha pôs
	 * `categoryToScroll` na categoria pedida; o scrollspy continua com o mesmo valor e o setter
	 * não roda. Sem isto a pill cancelada seguia destacada, e o `<select>` ficava na opção
	 * cancelada — e escolher a mesma opção de novo não dispara `change`.
	 */
	showActive(value: string) {
		if (!this.markActive(value) || (this.cdr as ViewRef).destroyed) return;
		this.cdr.detectChanges();
	}

	/** Marca `value` como a categoria ativa; `false` se já era. */
	private markActive(value: string): boolean {
		if (value == null || value === '' || value === this.categoryToScroll) return false;
		this.categoryToScroll = value;
		setTimeout(() => this.scrollActivePillIntoView(), 50);
		return true;
	}

	selectCategory(value: string) {
		this.categoryToScroll = value;
		this.scrollToCategory.emit(value);
	}

	scrollActivePillIntoView() {
		if (this.stickyMode || !this.pillsWrapper?.nativeElement) return;
		const active = this.pillsWrapper.nativeElement.querySelector('.category-pill--active') as HTMLElement;
		if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
	}

	onSelectChange(ev: Event) {
		const value = (ev.target as HTMLSelectElement).value;
		this.categoryToScroll = value;
		this.scrollToCategory.emit(value);
	}

	/**
	 * Toque num segmento do layout legado. O `ion-segment` só emite `ionChange` quando o valor
	 * MUDA, então tocar de novo na categoria já marcada não fazia nada — e é justamente o que
	 * a pessoa faz quando o primeiro toque parece não ter pegado (a categoria ainda montando)
	 * ou depois de ter rolado para longe dela. O `(click)` do botão roda antes do listener do
	 * segmento (o evento sobe do botão para o segmento), então aqui `categoryToScroll` ainda é
	 * o valor anterior: igual = toque repetido, emite; diferente = o `ionChange` emite.
	 */
	reselectSegment(value: string) {
		if (value === this.categoryToScroll) this.scrollToCategory.emit(value);
	}

	changeCategory(ev: any) {
		const value = ev?.detail?.value;
		if (value != null) {
			this.categoryToScroll = value;
			this.scrollToCategory.emit(value);
		}
	}

	trackById(_index: number, category: any) {
		return category ? category.id : _index;
	}

	truncate(text: string, maxLength = 35): string {
		const isMobile = window.innerWidth < 768;
		maxLength = isMobile ? 30 : 60;
		if (!text || text.length <= maxLength) return text;
		return text.slice(0, maxLength).replace(/\s+$/, '') + '…';
	}

	showCategory(items) {
		// if(category.isSelfService) return false;

		// const items = category.products;

		if(!items) return false;

		return items.some(item => !item.unavailable && !item.unavailableByTemplate && item.totalCalcType !== 'WEIGHT');
	}

}
